import type { ReactNode } from 'react'
import { useState } from 'react'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'

/** Above this many items the list collapses to the most recent + a "N more" stack. */
const GROUP_THRESHOLD = 1

function GroupHeader({
  title,
  count,
  expanded,
  collapsible,
  onToggle,
  toggleLabel,
}: {
  title: string
  count: number
  expanded: boolean
  collapsible: boolean
  onToggle: () => void
  toggleLabel: string
}) {
  const label = (
    <>
      <span className="text-sm font-medium text-gray-900">{title}</span>
      <span className="flex h-3.5 items-center rounded-full bg-gray-200 px-1.5">
        <span className="text-2xs-plus font-medium text-gray-500">{count}</span>
      </span>
      {collapsible && (
        <ChevronDownIcon
          className={cn(
            'size-3.5 text-gray-500 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      )}
    </>
  )

  if (!collapsible) {
    return <div className="flex items-center gap-1 self-start">{label}</div>
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={toggleLabel}
      className="flex cursor-pointer items-center gap-1 self-start"
    >
      {label}
    </button>
  )
}

/** Stacked "papers" teaser shown beneath the latest item when the list is grouped. */
function MoreItemsStack({
  label,
  ariaLabel,
  onExpand,
}: {
  label: string
  ariaLabel: string
  onExpand: () => void
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={ariaLabel}
      className="relative w-full cursor-pointer"
    >
      <div className="w-full px-2">
        <div className="h-1.75 rounded-b-lg border border-t-0 border-gray-200 bg-white" />
      </div>
      <div className="w-full px-4">
        <div className="h-1.75 rounded-b-lg border border-t-0 border-gray-200 bg-white" />
      </div>
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2.5 text-2xs leading-none tracking-widest whitespace-nowrap text-gray-500">
        {label}
      </span>
    </button>
  )
}

export interface GroupedItemsProps<T> {
  className?: string
  title: string
  toggleLabel: string
  items: T[]
  getKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  /** Label for the collapsed stack, given the count of hidden items. */
  moreLabel: (count: number) => string
  /** Accessible label for the expand control, given the count of hidden items. */
  showMoreLabel: (count: number) => string
}

/** Renders a titled, count-badged list that collapses to the most recent item
 *  plus a "N more" stack once it grows past {@link GROUP_THRESHOLD}. */
export function GroupedItems<T>({
  className,
  title,
  toggleLabel,
  items,
  getKey,
  renderItem,
  moreLabel,
  showMoreLabel,
}: GroupedItemsProps<T>) {
  const [showAll, setShowAll] = useState(false)
  const collapsible = items.length > GROUP_THRESHOLD
  const grouped = collapsible && !showAll
  const visible = grouped ? items.slice(0, 1) : items
  const hidden = items.length - 1

  return (
    <div className={cn('flex flex-col gap-2.5', className)}>
      <GroupHeader
        title={title}
        count={items.length}
        expanded={showAll}
        collapsible={collapsible}
        onToggle={() => setShowAll((value) => !value)}
        toggleLabel={toggleLabel}
      />
      <div className={cn('flex flex-col', !grouped && 'gap-2.5')}>
        {visible.map((item) => (
          <div key={getKey(item)}>{renderItem(item)}</div>
        ))}
        {grouped && (
          <MoreItemsStack
            label={moreLabel(hidden)}
            ariaLabel={showMoreLabel(hidden)}
            onExpand={() => setShowAll(true)}
          />
        )}
      </div>
    </div>
  )
}
